import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "react-toastify";
import { getActiveRubric, getRubricVersion } from "../../services/rubricService";
import {
  cancelObservation,
  createObservation,
  getFocusAreas,
  getMyObservations,
  getObservation,
  submitObservation,
  updateObservation,
} from "../../services/observationService";
import { getUser, getUsers } from "../../services/userService";
import { getGroups } from "../../services/groupService";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import useOrgScope from "../../utils/hooks/useOrgScope";
import useDomainSubjects from "../../utils/hooks/useDomainSubjects";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import {
  FINALIZED_STATUSES,
  OBSERVATION_STATUS,
  OPEN_STATUSES,
} from "../../utils/constants/observations";
import { UserContext } from "../../utils/contexts/UserContext";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import { normalizeApiFocusAreas } from "../../utils/helpers/focusAreas";
import { fullName as teacherName } from "../../utils/helpers/userDisplay";
import Avatar from "../../components/Avatar/Avatar";
import { RequiredMark } from "../../components/forms/Field";
import FieldSelect from "../../components/forms/FieldSelect";
import InlineRetry from "../../components/forms/InlineRetry";
import ConfirmModal from "../Templates/ConfirmModal";
import FocusStandardChips, {
  visitFocusStandards,
} from "../../components/scheduling/FocusStandardChips";

import { formatApiError } from "../../utils/helpers/apiErrors";
import { apiErrorCode } from "../../components/timetable/runErrors";
import { guardUnsavedWork } from "../../utils/helpers/unsavedWork";
import {
  observationDraftKey,
  removeObservationDraft,
  restoreObservationDraft,
  saveObservationDraft,
} from "../../utils/helpers/observationDraft";
import { optionSetsByUuid } from "../Templates/templateTree";
import { rubricNeedsSubject, rulesOut, standardAppliesTo } from "../../utils/helpers/standardScope";
import {
  daysLate,
  formatDateTime,
  formatTime,
  formatWeekdayMonthDay,
  parseLocalISODate,
  toLocalISODate,
} from "../../utils/helpers/dateDisplay";
import HiddenFollowUpsBadge from "../../components/HiddenFollowUpsBadge/HiddenFollowUpsBadge";
import {
  CONDUCT_BTN,
  CONDUCT_ICON_PATH,
  CONDUCT_SOON_BTN,
  awaitsPeerConfirmation,
  isConductDue,
} from "../../components/scheduling/conductAction";

// A question is visible only when every visibility condition passes; a
// condition passes when its dependency question is answered with the
// expected option (equals) or with anything else (not_equals).
// Semantics intentionally match the backend submit validator
// (observations/services/visibility.py) and the editor preview
// (pages/Templates/templateTree.js computeVisibleKeys) — they operate on
// different data shapes (UUID rows / API tree / editor keys), so each keeps
// its own implementation; keep gating rule changes in sync across all three.
const isQuestionVisible = (question, answers) => {
  const conditions = question.visibility_conditions || [];
  return conditions.every((cond) => {
    const dep = answers[cond.depends_on_question];
    if (!dep || !dep.selected_option) return false;
    if (cond.operator === "not_equals") {
      return dep.selected_option !== cond.expected_option;
    }
    return dep.selected_option === cond.expected_option;
  });
};

// Drop answers for questions that are no longer visible. Repeats to a
// fixpoint because hiding one question can hide others that depend on it.
const pruneHiddenAnswers = (standards, answers) => {
  let current = answers;
  let changed = true;
  while (changed) {
    changed = false;
    const next = {};
    for (const std of standards) {
      for (const q of std.questions) {
        if (!current[q.uuid]) continue;
        if (isQuestionVisible(q, current)) {
          next[q.uuid] = current[q.uuid];
        } else {
          changed = true;
        }
      }
    }
    current = next;
  }
  return current;
};

// The answers that still mean something on `rubric`: the question is in it and
// the chosen option is one of that question's. A restored draft is checked
// against the rubric that actually loads — a version published since the draft
// was saved would otherwise fail the whole submit on unknown question uuids.
const answersOnRubric = (rubric, answers) => {
  const optionSets = optionSetsByUuid(rubric?.answer_option_sets);
  const kept = {};
  (rubric?.standards || []).forEach((std) =>
    std.questions.forEach((q) => {
      const ans = answers[q.uuid];
      if (!ans) return;
      const options = optionSets[q.answer_option_set]?.options || [];
      if (ans.selected_option && !options.some((o) => o.uuid === ans.selected_option)) return;
      kept[q.uuid] = ans;
    }),
  );
  return pruneHiddenAnswers(rubric?.standards || [], kept);
};

// "10:42" for a draft saved today, the full date and time otherwise.
const savedAtLabel = (savedAt) =>
  toLocalISODate(new Date(savedAt)) === toLocalISODate(new Date())
    ? formatTime(savedAt)
    : formatDateTime(savedAt);

// A server draft the form no longer files against. Soft-cancelled rather than
// left behind as an orphan; best-effort, since nothing waits on it.
const supersedeDraft = (uuid) =>
  cancelObservation({ uuid, body: { reason: "superseded" } }).catch(() => {});

// A header field a booked visit owns. Rendered as an inert box rather than a
// disabled <select>: there is nothing to open, and a greyed-out control invites
// clicking at it. The lock icon is the whole explanation — a banner sentence
// spelling out which fields were locked read "the class comes from this visit"
// beside a class of "—", and was taken out.
function LockedField({ value }) {
  return (
    <div className="flex items-center gap-2 w-full bg-slate-100 border border-slate-200 rounded-xl px-3 py-3">
      <svg
        className="w-3.5 h-3.5 text-slate-400 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
      <span className="min-w-0 truncate font-bold text-sm text-slate-700">{value}</span>
    </div>
  );
}

// ObservationUpdateSerializer refuses a header write past SCHEDULED, so an
// IN_PROGRESS visit (6 of them in the live data) can never record a topic.
const HEADER_WRITABLE_STATUSES = new Set([
  OBSERVATION_STATUS.SCHEDULED,
  OBSERVATION_STATUS.DRAFT,
]);

// A booked visit: its day and period, amber once that day has passed — paired
// with the number of days, because a hue on its own is not a fact anyone can
// act on. Heads both conduct mode and the open-visit hint.
function VisitBanner({ visit, title, subline, actions, children, t }) {
  const late = daysLate(visit.scheduled_date);
  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 ${
        late > 0 ? "border-amber-200 bg-amber-50/70" : "border-indigo-200 bg-indigo-50/60"
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
              late > 0 ? "bg-amber-100 text-amber-600" : "bg-indigo-100 text-indigo-600"
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
                late > 0 ? "text-amber-700" : "text-indigo-600"
              }`}
            >
              {title}
            </p>
            <p className="mt-1 text-sm font-bold text-slate-900">
              {[
                visit.scheduled_date
                  ? formatWeekdayMonthDay(parseLocalISODate(visit.scheduled_date))
                  : t("upcoming.noDate"),
                visit.period ? t("upcoming.period", { period: visit.period }) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <p className="mt-0.5 text-xs font-medium text-slate-600">{subline}</p>
            {late > 0 && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-800">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
                {t("observation.conductingOverdue", { count: late })}
              </p>
            )}
            {children}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 shrink-0 pl-12 sm:pl-0">{actions}</div>
        )}
      </div>
    </div>
  );
}

// Why the visit was booked: the standards it was asked to look at and the
// booking's note. Both were set when the visit was scheduled or requested and
// shown on the Upcoming tab, then hidden on the one screen where the observer
// actually needs them. Focus uuids resolve against the visit's own rubric
// version, which is the one conduct mode loads, and only those the lesson's
// `subject` is scored on are listed — the form shows no card for the rest.
function VisitBrief({ visit, rubric, subject, t }) {
  const focus = visitFocusStandards(rubric, visit.focus_standards, subject);
  const note = (visit.general_comment || "").trim();
  if (!focus.length && !note) return null;
  return (
    <div className="mt-3 space-y-2.5">
      {focus.length > 0 && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
            {t("upcoming.focusLabel")}
          </p>
          <FocusStandardChips standards={focus} />
        </div>
      )}
      {note && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
            {t("upcoming.visitNote")}
          </p>
          <p className="text-sm text-slate-700 whitespace-pre-line break-words">{note}</p>
        </div>
      )}
    </div>
  );
}

// The submit's refusals over the lesson subject, by the `code` the server
// sends with its domain_subject error.
const SUBJECT_REFUSALS = {
  subject_required: "observation.subjectRequired",
  no_standard_for_subject: "observation.noStandardForSubject",
};

// What the form still needs before it can be answered: one info row above the
// standards, in the tone of the step it waits on.
const HINT_TONES = {
  indigo: { box: "border-indigo-200 bg-indigo-50 text-indigo-800", icon: "text-indigo-500" },
  amber: { box: "border-amber-200 bg-amber-50 text-amber-800", icon: "text-amber-500" },
};

function FormHint({ tone, children }) {
  const { box, icon } = HINT_TONES[tone];
  return (
    <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold ${box}`}>
      <svg className={`w-5 h-5 shrink-0 ${icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {children}
    </div>
  );
}

// Discarding a restored draft remounts the form rather than resetting two dozen
// pieces of state by hand: a fresh mount that restores nothing IS the empty
// form, by construction.
export default function Observation() {
  const [epoch, setEpoch] = useState(0);
  return (
    <ObservationForm key={epoch} restore={epoch === 0} onDiscard={() => setEpoch((n) => n + 1)} />
  );
}

function ObservationForm({ restore, onDiscard }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext) || {};
  const [searchParams] = useSearchParams();
  const initialTeacher = searchParams.get("teacher") || "";
  // ?visit= turns this page from "file a new observation" into "conduct the
  // visit that was already booked". Without it a scheduled visit could never
  // be closed: the form always created a fresh draft, so the booked row stayed
  // behind as overdue forever while its answers landed on a different
  // observation.
  const visitUuid = searchParams.get("visit") || "";
  const isConducting = Boolean(visitUuid);
  const [visit, setVisit] = useState(null);

  // The work this browser kept from an earlier, unsent attempt (see
  // utils/helpers/observationDraft). Read once, at mount, because it seeds the
  // initial state below — so the first rubric fetch already targets the
  // restored teacher's school instead of loading the wrong one first.
  const [restored] = useState(() =>
    restore
      ? restoreObservationDraft({ userUuid: user?.uuid, visitUuid, teacherUuid: initialTeacher })
      : null,
  );
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Where this form's work is stored, so a draft that moves (another teacher
  // picked, or the booked visit opened from the hint) does not leave its old
  // copy behind.
  const draftKeyRef = useRef(null);
  // Set once the work has nowhere left to go — submitted, discarded, or a visit
  // already closed — so no later render writes it back.
  const draftClosedRef = useRef(false);
  // The server draft an earlier attempt created, kept in the stored copy until
  // the already-filed check below has settled what it is.
  const staleDraftRef = useRef(restored?.draftUuid || null);
  // The class list is fetched after mount, and the effect that drops a class
  // missing from it would clear a restored pick before the list arrived. Held
  // here instead and applied when the list lands.
  const pendingGroupRef = useRef(restored?.group || "");

  const [rubric, setRubric] = useState(null);
  // The school the on-screen rubric was fetched for (null = the observer's
  // own default). While it differs from the picked teacher's school the form
  // stays readable but cannot be submitted — see rubricStale.
  const [rubricFor, setRubricFor] = useState(undefined);
  const [rubricFailed, setRubricFailed] = useState(false);
  const [rubricReload, setRubricReload] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [isTeachersLoading, setIsTeachersLoading] = useState(true);
  const [teachersError, setTeachersError] = useState(false);
  const [teachersReload, setTeachersReload] = useState(0);
  const [groups, setGroups] = useState([]);
  const [classesError, setClassesError] = useState(false);
  const [classesReload, setClassesReload] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // The selected teacher is held as the ROW, not a uuid resolved against
  // `teachers`: the list is now one server-searched page, so a lookup would
  // lose the selection (blank trigger, empty header card) the moment the query
  // stopped matching the person already chosen.
  const [selectedTeacher, setSelectedTeacher] = useState(
    () => (!isConducting && restored?.teacher) || null,
  );
  const [teacherQuery, setTeacherQuery] = useState("");
  const debouncedTeacherQuery = useDebouncedValue(teacherQuery, 300);
  const [selectedGroup, setSelectedGroup] = useState("");
  // The lesson's subject decides which of the rubric's standards it is scored
  // on (utils/helpers/standardScope). "" = not picked; see lessonSubject.
  const [selectedSubject, setSelectedSubject] = useState(restored?.subject || "");
  const subjectCatalog = useDomainSubjects();
  // Set by a submit refused over the subject; the field is marked while the
  // problem lasts. On a phone it sits a whole form above the button.
  const [subjectRefused, setSubjectRefused] = useState(false);
  const subjectFieldRef = useRef(null);
  const [lessonTopic, setLessonTopic] = useState(restored?.topic || "");
  // Two write-ups with different audiences: `sharedNote` reaches the observed
  // teacher, `privateNote` never does (see observations/scoping.py).
  const [sharedNote, setSharedNote] = useState(restored?.sharedNote || "");
  const [privateNote, setPrivateNote] = useState(restored?.privateNote || "");
  const [openComments, setOpenComments] = useState({});
  const [selectedDate, setSelectedDate] = useState(
    // local "today" (not UTC, which can be a day off)
    () => restored?.date || toLocalISODate(new Date()),
  );
  // answers: { [questionUuid]: { selected_option, text_answer } }
  const [answers, setAnswers] = useState(() => restored?.answers || {});
  // Latest values for the rubric fetch below, which resolves after the render
  // that started it and must compare against what is on screen now.
  const rubricRef = useRef(rubric);
  rubricRef.current = rubric;
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reuse the created draft across retries so a failed submit doesn't leave
  // (and multiply) orphaned draft observations on each Submit click.
  const [draftUuid, setDraftUuid] = useState(null);
  const [focusResult, setFocusResult] = useState(null);
  const [focusError, setFocusError] = useState(false);
  const [focusReload, setFocusReload] = useState(0);
  const [isFocusExpanded, setIsFocusExpanded] = useState(false);
  // Visits already booked for the picked teacher with the caller as observer.
  // Emptied by "a different lesson"; the next teacher pick refetches.
  const [openVisits, setOpenVisits] = useState([]);

  const selectedTeacherUuid = selectedTeacher?.uuid || null;
  // The school the class picker is scoped to — an observation records a teacher
  // teaching one of their OWN school's classes.
  const selectedSchoolUuid = selectedTeacher?.organization?.uuid || null;
  const visitVersionUuid = visit?.template_version?.uuid || null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The booked visit, loaded before anything else in conduct mode: it names the
  // teacher, the class, and the rubric version the answers get validated
  // against.
  useEffect(() => {
    if (!visitUuid) return undefined;
    const controller = new AbortController();
    getObservation({ uuid: visitUuid, signal: controller.signal })
      .then((row) => {
        // A finalized or cancelled visit has nothing left to conduct — filing
        // against it would be a second record of one lesson, and the server
        // refuses it anyway (services/submission.py). Turn back at the door
        // rather than after thirty answers.
        if (
          FINALIZED_STATUSES.has(row.status) ||
          row.status === OBSERVATION_STATUS.CANCELLED
        ) {
          // Whatever was kept for it can never be submitted now — most often
          // because an earlier submit did land and only its response was lost.
          draftClosedRef.current = true;
          removeObservationDraft(observationDraftKey({ userUuid: user?.uuid, visitUuid }));
          toast.info(t("observation.visitClosed"));
          navigate("/my-observations", { replace: true });
          return;
        }
        // DELIBERATELY NARROWER THAN THE SERVER, and this is the one place that
        // difference is written down. `can_manage` (observations/scoping.py)
        // also admits anyone holding observations.observations.manage in the
        // school — every school admin — so /submit/ would accept them. But
        // `submit_observation` never rewrites ObservationObserver: a visit an
        // admin conducts on somebody else's booking stays filed under the
        // BOOKED observer, putting an evaluation in the record over a person
        // who was not in the room and crediting them for it on /coverage. So
        // conducting belongs to whoever the visit is assigned to; an admin's
        // verbs on another person's booking are reschedule and cancel. Widen
        // this only together with a way to hand the visit over first.
        //
        // The observed teacher can legitimately READ their own visit, so
        // without this check they could open the form and be refused at the end.
        // Held in a local first: comparing two `undefined`s is how a row with
        // no creator would match a caller with no uuid and let anyone through.
        const mineUuid = user?.uuid;
        const mayConduct =
          Boolean(mineUuid) &&
          (row.created_by?.uuid === mineUuid ||
            (row.observers || []).some((o) => o.observer?.uuid === mineUuid));
        if (!mayConduct) {
          toast.error(t("observation.visitNotYours"));
          navigate("/my-observations", { replace: true });
          return;
        }
        setVisit(row);
        // `observed_teacher` is an AccountMini and carries no `organization`,
        // while the picker's rows (from /auth/users/) do — so without this the
        // header card falls back to printing the teacher's email address, and
        // every school-derived value below silently reads null. The visit's own
        // organization IS that school: it is the school the lesson is filed on.
        setSelectedTeacher(
          row.observed_teacher
            ? { ...row.observed_teacher, organization: row.organization || null }
            : null,
        );
        // A topic typed on the new-observation form this page was switched
        // from (the open-visit hint) is kept — booked visits arrive without
        // one. Not on an in_progress visit: its header refuses the PATCH that
        // would carry it, and the submit would fail on it at the very end.
        // A topic restored for THIS visit is the observer's newer word on a
        // header that still takes one, so it wins over the booking's.
        const ownDraft = restored?.visit === visitUuid ? restored : null;
        if (row.domain_subject) setSelectedSubject(row.domain_subject.uuid);
        setLessonTopic((prev) => {
          if (!HEADER_WRITABLE_STATUSES.has(row.status)) return row.lesson_topic || "";
          return ownDraft?.topic ? prev : row.lesson_topic || prev;
        });
        // A visit is routinely conducted later than it was booked, so the date
        // stays the observer's to change; it becomes `observed_at` on submit,
        // while `scheduled_date` keeps saying when it was meant to happen. A
        // restored date is that change, already made.
        if (row.scheduled_date && !ownDraft?.date) setSelectedDate(row.scheduled_date);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setLoadError(formatApiError(err, t("observation.visitLoadFailed")));
        // The rubric fetch below never starts without a visit, so nothing else
        // would ever take the page out of its skeleton.
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [visitUuid, user?.uuid, navigate, t, restored]);

  // The rubric follows the OBSERVED teacher's school, not the observer's own
  // — a head-office observer's default school can differ from every teacher
  // they pick, and an observation filed on the wrong rubric silently drops
  // out of that school's rubric-aware analytics. No teacher chosen yet falls
  // back to the caller's own school (the backend default).
  useEffect(() => {
    // Conduct mode has nothing to fetch until the visit says which version it
    // was booked against.
    if (isConducting && !visitVersionUuid) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    setRubricFailed(false);
    // A booked visit is answered on ITS OWN version, never the school's current
    // one: submit_observation validates every answer against
    // observation.template_version, so a rubric published between the booking
    // and the lesson would reject the whole submission on unknown question
    // uuids. `translations: false` keeps the authoring payload off a screen
    // that renders one language.
    const request = isConducting
      ? getRubricVersion({
          uuid: visitVersionUuid,
          translations: false,
          signal: controller.signal,
        })
      : getActiveRubric({
          params: { organization: selectedSchoolUuid || undefined },
          signal: controller.signal,
        });
    request
      .then((next) => {
        // Answers are keyed on the rubric's question uuids, so only those the
        // loaded rubric still has survive it. Picking a teacher at a school on
        // a different rubric drops them all — questions belong to one version
        // — and a restored draft loses only what was removed since it was saved.
        const prev = rubricRef.current;
        const kept = next ? answersOnRubric(next, answersRef.current) : answersRef.current;
        if (Object.keys(kept).length < Object.keys(answersRef.current).length) {
          const switched = prev && next && prev.uuid !== next.uuid;
          toast.info(t(switched ? "observation.rubricChanged" : "observation.draftRubricChanged"));
          setAnswers(kept);
        }
        setRubric(next);
        setRubricFor(selectedSchoolUuid);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        const message = formatApiError(err, t("observation.loadFailed"));
        // A form already in use keeps its rubric on screen, blocked from
        // submitting, with a retry beside the button; only a first load has
        // nothing else to show.
        if (rubricRef.current) setRubricFailed(true);
        else setLoadError(message);
      })
      // An aborted request settles after its replacement has started — its
      // finally must not clear the replacement's loading state.
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [t, isConducting, visitVersionUuid, selectedSchoolUuid, rubricReload]);

  // True while the rubric on screen belongs to a different school than the
  // teacher being observed: the refetch is in flight, or it failed. Filing
  // then would score this teacher on another school's rubric. Conduct mode is
  // exempt — its version is pinned by uuid, and the teacher is locked, so the
  // form can never drift between schools mid-edit.
  const rubricStale =
    !isConducting && Boolean(rubric) && rubricFor !== selectedSchoolUuid;

  // Which questions apply depends on the teacher's school, so nothing is
  // answerable until one is chosen. Before that the form shows the caller's
  // default rubric — for an admin with no school of their own, whatever
  // version was published last — and answers given on it would be dropped
  // the moment the real rubric arrived.
  const canAnswer = Boolean(selectedTeacher) && !rubricStale;

  // A visit whose header the server will no longer take (see
  // HEADER_WRITABLE_STATUSES) says so by being read-only, rather than taking
  // the typing and trading it for a 400 at the end of the form.
  const canEditVisitHeader =
    !isConducting || HEADER_WRITABLE_STATUSES.has(visit?.status);

  // A visit booked on a period with no timetable lesson (the open grid) carries
  // no class, and nothing downstream can give it one — Reschedule takes the
  // class only from a lesson, which is exactly what that teacher lacks. Locking
  // "—" there left the observer no way to say which class they sat in, so only
  // a class the visit actually names is locked.
  const classLocked = isConducting && (Boolean(visit?.group) || !canEditVisitHeader);
  // A booking that names its subject keeps it. Unlike the class it stays
  // pickable on a visit already in progress: it is sent with the answers.
  const subjectLocked = isConducting && Boolean(visit?.domain_subject);
  // A teacher with one teaching subject stands in for it until the observer
  // picks — the commonest case needs no click at all.
  const soleSubject =
    selectedTeacher?.subjects?.length === 1 ? selectedTeacher.subjects[0].uuid : "";
  const lessonSubject = selectedSubject || soleSubject;

  // Only users observations may be recorded about (teacher role +
  // can_be_evaluated) belong in the picker. The search is the SERVER's: a
  // network of 2500 teachers overflows the 1000-row page cap, and the overflow
  // arrives as a short list rather than an error, so filtering what arrived
  // would quietly hide the rest.
  //
  // No auto-select: the form starts with nobody chosen rather than guessing
  // the newest-synced account in the whole network — a deep link (below) still
  // preselects the named teacher.
  //
  // The school picked in the app header narrows the roster; no pick means
  // everyone in scope.
  const { pickedOrgUuid, pickPending, subtreeOf } = useOrgScope();
  useEffect(() => {
    // A booked visit's teacher is locked, so there is no roster to fill.
    if (isConducting || pickPending) return undefined;
    const controller = new AbortController();
    setIsTeachersLoading(true);
    getUsers({
      params: {
        observable: "true",
        search: debouncedTeacherQuery || undefined,
        page_size: MAX_PAGE_SIZE,
        organization: pickedOrgUuid || undefined,
      },
      signal: controller.signal,
    })
      .then((data) => {
        const list = (data?.results || []).filter((u) => u.is_active);
        setTeachers(list);
        setTeachersError(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setTeachers([]);
        setTeachersError(true);
      })
      .finally(() => {
        // An aborted request's finally lands after its replacement has started;
        // clearing the flag then shows the old roster as settled.
        if (!controller.signal.aborted) setIsTeachersLoading(false);
      });
    return () => controller.abort();
  }, [isConducting, pickPending, pickedOrgUuid, debouncedTeacherQuery, teachersReload]);

  // A teacher chosen under one school does not survive a switch to another: the
  // header would name school B over an observation filed on school A's teacher.
  // Judged by membership in the picked subtree rather than by "the pick
  // changed", because a pick restored from sessionStorage also changes once the
  // subtree loads, and that must not drop a teacher a ?teacher= link preselected.
  useEffect(() => {
    if (isConducting || !pickedOrgUuid || !selectedSchoolUuid) return;
    if (!subtreeOf(pickedOrgUuid).includes(selectedSchoolUuid)) setSelectedTeacher(null);
  }, [isConducting, pickedOrgUuid, selectedSchoolUuid, subtreeOf]);

  // A deep link names a teacher who may sit anywhere in a 2500-row roster, so
  // resolve them directly instead of hoping they are on the page that arrived.
  // `observable` narrows retrieve too, so someone no longer observable 404s and
  // stays unselected rather than falling back to an arbitrary first teacher.
  // A restored teacher is re-read the same way: the stored row can be a week
  // old, and the school it names decides which rubric loads.
  const teacherToResolve = initialTeacher || restored?.teacher?.uuid || "";
  useEffect(() => {
    // A booked visit already names who is being observed. A URL carrying both
    // params must not let ?teacher= quietly move the answers onto someone else.
    if (!teacherToResolve || isConducting) return undefined;
    const controller = new AbortController();
    getUser({
      uuid: teacherToResolve,
      params: { observable: "true" },
      signal: controller.signal,
    })
      .then((teacher) => {
        // Never over a teacher picked while this was in flight.
        if (teacher?.is_active) {
          setSelectedTeacher((prev) => (!prev || prev.uuid === teacher.uuid ? teacher : prev));
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [teacherToResolve, isConducting]);

  // Classes follow the chosen teacher's school. Fetching the whole subtree and
  // narrowing here hit the same cap: a network's classes overflow 1000 rows, so
  // a teacher whose school sorted past the cut got an empty class picker.
  useEffect(() => {
    // Nothing to pick from when the visit names its class: fetching a school's
    // entire class list would be a 1000-row request whose only consumer is a
    // picker that isn't on screen.
    if (classLocked || !selectedSchoolUuid) {
      setGroups([]);
      setClassesError(false);
      return undefined;
    }
    const controller = new AbortController();
    setClassesError(false);
    setGroups([]);
    getGroups({
      params: { organization: selectedSchoolUuid, page_size: MAX_PAGE_SIZE },
      signal: controller.signal,
    })
      .then((data) => {
        const list = data?.results || [];
        setGroups(list);
        // A restored class, now that there is a list to find it in.
        const pending = pendingGroupRef.current;
        pendingGroupRef.current = "";
        if (pending && list.some((g) => g.uuid === pending)) setSelectedGroup(pending);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setGroups([]);
        setClassesError(true);
      });
    return () => controller.abort();
  }, [classLocked, selectedSchoolUuid, classesReload]);

  useEffect(() => {
    if (!selectedTeacherUuid) {
      setFocusResult(null);
      setFocusError(false);
      return undefined;
    }
    const controller = new AbortController();
    setFocusError(false);
    getFocusAreas({
      params: { teacher: selectedTeacherUuid },
      signal: controller.signal,
    })
      .then((data) => {
        setFocusResult(data);
        setFocusError(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setFocusResult(null);
        setFocusError(true);
      });
    return () => controller.abort();
  }, [selectedTeacherUuid, focusReload]);

  // A new observation of a teacher the caller already has a booked visit with
  // is almost always THAT visit being written up the long way round — and doing
  // it here leaves the booking open, counted as missed, beside a second record
  // of the same lesson. Same query as MyObservations' «Назначено мне», narrowed
  // to this teacher, minus peer visits the colleague has not accepted — the
  // Upcoming tab does not offer those for conducting either. Oldest first: the
  // most overdue booking is the likeliest one being filed late.
  // Best-effort: a failed lookup just shows no hint.
  useEffect(() => {
    setOpenVisits([]);
    if (isConducting || !selectedTeacherUuid) return undefined;
    const controller = new AbortController();
    getMyObservations({
      params: {
        role: "main_observer",
        status__in: [...OPEN_STATUSES].join(","),
        observed_teacher__uuid: selectedTeacherUuid,
        ordering: "scheduled_date",
      },
      signal: controller.signal,
    })
      .then((data) =>
        setOpenVisits((data?.results || []).filter((v) => !awaitsPeerConfirmation(v))),
      )
      .catch(() => {});
    return () => controller.abort();
  }, [isConducting, selectedTeacherUuid]);

  // The booking on the form's date when there is one, else the oldest.
  const openVisit =
    openVisits.find((v) => v.scheduled_date === selectedDate) || openVisits[0];

  // Switching to conduct mode is a navigation within this same route, so the
  // component is NOT remounted: answers typed so far survive whenever the
  // visit's rubric version is the one already on screen (the rubric effect
  // clears them, with a toast, when it isn't). A draft left by a failed submit
  // would be orphaned — conduct mode never cancels one — so it goes here.
  const conductOpenVisit = () => {
    if (draftUuid) {
      supersedeDraft(draftUuid);
      setDraftUuid(null);
    }
    navigate(`/observation?visit=${openVisit.uuid}`);
  };

  const optionSetsByUuid = useMemo(() => {
    const map = {};
    (rubric?.answer_option_sets || []).forEach((set) => {
      map[set.uuid] = set;
    });
    return map;
  }, [rubric]);

  const optionsFor = useCallback(
    (question) => optionSetsByUuid[question.answer_option_set]?.options || [],
    [optionSetsByUuid],
  );

  // Answers live on every standard of the rubric, so switching the subject
  // back and forth never loses what was already ticked; only the standards
  // this lesson is scored on are shown, counted and sent.
  const allStandards = useMemo(
    () => [...(rubric?.standards || [])].sort((a, b) => a.order - b.order),
    [rubric],
  );
  const needsSubject = rubricNeedsSubject(allStandards);
  const subjectMissing = needsSubject && !lessonSubject;
  const standards = useMemo(
    () => allStandards.filter((std) => standardAppliesTo(std, lessonSubject)),
    [allStandards, lessonSubject],
  );
  // A subject outside every standard's scope leaves nothing to score, and the
  // server refuses such a lesson.
  const noStandardApplies = !subjectMissing && allStandards.length > 0 && standards.length === 0;
  const subjectInvalid = subjectRefused && (subjectMissing || noStandardApplies);

  // Visibility computed once per answers change and reused by everything
  // below (domain blocks, heatmap summary, badge counts, submit collection).
  const visibleByStdUuid = useMemo(() => {
    const map = {};
    standards.forEach((std) => {
      map[std.uuid] = [...std.questions]
        .sort((a, b) => a.order - b.order)
        .filter((q) => isQuestionVisible(q, answers));
    });
    return map;
  }, [standards, answers]);

  const visibleQuestionsFor = useCallback(
    (standard) => visibleByStdUuid[standard.uuid] || [],
    [visibleByStdUuid],
  );

  // gate question -> questions that depend on it (any standard); used to
  // advertise hidden follow-ups so observers know more questions appear
  // behind a gating answer.
  const dependentsByUuid = useMemo(() => {
    const map = {};
    standards.forEach((std) =>
      std.questions.forEach((q) =>
        (q.visibility_conditions || []).forEach((c) => {
          (map[c.depends_on_question] = map[c.depends_on_question] || []).push(q.uuid);
        }),
      ),
    );
    return map;
  }, [standards]);

  const visibleUuids = useMemo(
    () => new Set(Object.values(visibleByStdUuid).flat().map((q) => q.uuid)),
    [visibleByStdUuid],
  );

  // A focus area names the standard of a PAST answer, maybe on an earlier
  // version, so today's standard of its lineage decides whether this lesson
  // is scored on it. One the subject rules out has no card on the form and is
  // no focus here.
  const applicableAreas = useMemo(
    () =>
      (focusResult?.areas || []).filter(
        (a) => !rulesOut(allStandards, a.standard?.lineage, lessonSubject),
      ),
    [focusResult, allStandards, lessonSubject],
  );

  const focusAreas = useMemo(
    () => normalizeApiFocusAreas({ areas: applicableAreas }),
    [applicableAreas],
  );

  // UUIDs of the questions flagged as focus areas — used to badge them inline
  // on the form (not just in the summary at the top).
  const focusQuestionUuids = useMemo(
    () => new Set(applicableAreas.map((a) => a.question?.uuid).filter(Boolean)),
    [applicableAreas],
  );

  // Standards that contain at least one focus-area question — badged on the
  // domain header (mirrors the prototype's two-level focus indication).
  const focusStandardUuids = useMemo(
    () => new Set(applicableAreas.map((a) => a.standard?.uuid).filter(Boolean)),
    [applicableAreas],
  );

  const setAnswer = (question, value) => {
    setAnswers((prev) => {
      const next = { ...prev };
      // Merge, don't replace: the per-question comment lives on the same entry
      // and changing the verdict must not wipe the note explaining it.
      next[question.uuid] = {
        ...prev[question.uuid],
        ...(question.answer_type === "single_choice"
          ? { selected_option: value }
          : { text_answer: value }),
      };
      return pruneHiddenAnswers(allStandards, next);
    });
  };

  const setAnswerComment = (question, value) => {
    setAnswers((prev) => ({
      ...prev,
      [question.uuid]: { ...prev[question.uuid], comment: value },
    }));
  };

  // The chosen teacher rides along even when the roster is unfiltered and
  // doesn't (yet) include them — otherwise the trigger and header card would
  // blank on every page refetch. Gated on an EMPTY query: once the observer is
  // actually searching, a real "no match" must read as no match, not as the
  // one row that happens to already be selected.
  const teacherPool = useMemo(
    () =>
      selectedTeacher &&
      !debouncedTeacherQuery &&
      !teachers.some((u) => u.uuid === selectedTeacher.uuid)
        ? [selectedTeacher, ...teachers]
        : teachers,
    [teachers, selectedTeacher, debouncedTeacherQuery],
  );

  const pickTeacher = useCallback(
    (uuid) => {
      setSelectedTeacher(teacherPool.find((u) => u.uuid === uuid) || null);
      // A subject picked for one teacher's lesson says nothing about the next.
      setSelectedSubject("");
      setSubjectRefused(false);
    },
    [teacherPool],
  );

  const teacherOptions = useMemo(
    () =>
      teacherPool.map((teacher) => ({
        value: teacher.uuid,
        label: teacherName(teacher),
        sublabel: teacher.organization?.name,
        leading: (
          <Avatar
            user={teacher}
            className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 text-[10px] font-black shrink-0"
          />
        ),
      })),
    [teacherPool],
  );

  // A query still mid-debounce, or a completed search still fetching, both
  // read as "loading" to the picker — the stale roster stays on screen
  // (dimmed) instead of flashing a false "not found" while either is in
  // flight.
  const teacherSearchPending = teacherQuery !== debouncedTeacherQuery || isTeachersLoading;

  const groupOptions = useMemo(
    () =>
      groups.length === 0
        ? []
        : [
            { value: "", label: "—" },
            ...groups.map((g) => ({ value: g.uuid, label: g.name })),
          ],
    [groups],
  );

  // The teacher's own subjects first (the sort is stable, so each group
  // keeps the catalog's alphabetical order).
  const subjectOptions = useMemo(() => {
    const own = new Set((selectedTeacher?.subjects || []).map((s) => s.uuid));
    return [...subjectCatalog.options].sort(
      (a, b) => own.has(b.value) - own.has(a.value),
    );
  }, [subjectCatalog.options, selectedTeacher]);

  // When the teacher (hence school) changes, drop a class selection that no
  // longer belongs to the fetched set — never submit a cross-school pair.
  useEffect(() => {
    if (selectedGroup && !groups.some((g) => g.uuid === selectedGroup)) {
      setSelectedGroup("");
    }
  }, [groups, selectedGroup]);

  // A changed teacher, date or class keeps the answers — it is usually a
  // correction to the lesson being observed, and wiping 30 answers for a typo
  // in the date is the worst thing this form can do. What must not survive is
  // a draft already created by a failed submit: its create() baked in the OLD
  // teacher/class/topic, so a retry would still file against the original.
  // Soft-cancel it server-side rather than just dropping the local uuid, so
  // it doesn't linger as an orphaned draft.
  useEffect(() => {
    // Conduct mode never has a superseded draft — it fills the booked visit in
    // place. The guard is not redundant: the day `draftUuid` ever held the
    // visit's own uuid, changing the date would soft-CANCEL the very visit
    // being conducted.
    if (isConducting) return;
    setDraftUuid((prev) => {
      if (prev) supersedeDraft(prev);
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConducting, selectedTeacherUuid, selectedDate, selectedGroup, lessonTopic, rubric?.uuid]);

  // An observation is a lesson someone sat through: 30-odd answers that exist
  // only here until Submit. Reload, tab close and the two in-app exits that
  // discard it (a sidebar link, the language switcher) all route through this
  // one declaration — see utils/helpers/unsavedWork.
  // The draft kept below means leaving no longer LOSES the work, but it still
  // leaves the observation unsent, and that is what this guard asks about.
  // Measured against what the form was HANDED, not against empty: a conducted
  // visit arrives with its topic already filled, and a guard that fires the
  // instant the page opens is a guard people learn to click through. Outside
  // conduct mode the visit is null, so this is `Boolean(lessonTopic)` exactly.
  const hasUnsavedAnswers =
    Object.keys(answers).length > 0 ||
    lessonTopic !== (visit?.lesson_topic || "") ||
    Boolean(sharedNote) ||
    Boolean(privateNote);
  useEffect(
    () => guardUnsavedWork(hasUnsavedAnswers && !isSubmitting),
    [hasUnsavedAnswers, isSubmitting],
  );

  // Every change is written through at once rather than debounced: the moment
  // that matters is a phone putting the tab in the background, and a pending
  // timer is exactly what does not survive it. The whole form is a few KB.
  // Written only once there is something to lose, and never deleted for being
  // empty — a rubric switch clears answers on its own, and that must not take
  // the stored copy with it.
  const draftKey = observationDraftKey({
    userUuid: user?.uuid,
    visitUuid,
    teacherUuid: selectedTeacherUuid,
  });
  useEffect(() => {
    if (draftClosedRef.current || !draftKey || !hasUnsavedAnswers) return;
    if (draftKeyRef.current && draftKeyRef.current !== draftKey) {
      removeObservationDraft(draftKeyRef.current);
    }
    draftKeyRef.current = draftKey;
    saveObservationDraft(draftKey, {
      visit: visitUuid || null,
      teacher: isConducting ? null : selectedTeacher,
      date: selectedDate,
      group: selectedGroup || pendingGroupRef.current,
      subject: selectedSubject,
      topic: lessonTopic,
      answers,
      sharedNote,
      privateNote,
      draftUuid: draftUuid || staleDraftRef.current,
    });
  }, [
    draftKey,
    hasUnsavedAnswers,
    visitUuid,
    isConducting,
    selectedTeacher,
    selectedDate,
    selectedGroup,
    selectedSubject,
    lessonTopic,
    answers,
    sharedNote,
    privateNote,
    draftUuid,
  ]);

  const closeDraft = () => {
    draftClosedRef.current = true;
    removeObservationDraft(draftKeyRef.current);
    removeObservationDraft(draftKey);
  };

  const discardDraft = () => {
    closeDraft();
    if (draftUuid) supersedeDraft(draftUuid);
    onDiscard();
  };

  // The server draft an earlier attempt left behind. If its submit DID land —
  // only the response was lost on the way back — the lesson is already filed
  // and offering it again would make a second record of it, so this browser's
  // copy goes instead. Otherwise it is superseded like any other. It stays in
  // the stored copy until this has settled: a check that fails offline must
  // not erase the only trace of a submit that may have landed.
  useEffect(() => {
    const stale = staleDraftRef.current;
    if (!stale || isConducting) return undefined;
    const controller = new AbortController();
    getObservation({ uuid: stale, signal: controller.signal })
      .then((row) => {
        if (FINALIZED_STATUSES.has(row?.status)) {
          closeDraft();
          toast.info(t("observation.draftAlreadySent"));
          navigate("/my-observations", { replace: true });
          return;
        }
        if (row?.status !== OBSERVATION_STATUS.CANCELLED) supersedeDraft(stale);
        staleDraftRef.current = null;
      })
      .catch((err) => {
        if (err?.response?.status === 404) staleDraftRef.current = null;
      });
    return () => controller.abort();
    // Once, for the draft this mount restored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refuseSubject = (messageKey) => {
    toast.error(t(messageKey));
    setSubjectRefused(true);
    subjectFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    subjectFieldRef.current?.querySelector("button")?.focus({ preventScroll: true });
  };

  const handleSubmit = async () => {
    if (rubricStale) return;
    if (!selectedTeacher) {
      toast.error(t("observation.selectTeacherError"));
      return;
    }
    if (!rubric?.uuid) return;
    if (subjectMissing) {
      refuseSubject("observation.subjectRequired");
      return;
    }
    if (noStandardApplies) {
      refuseSubject("observation.noStandardForSubject");
      return;
    }

    // Collect answers for currently-visible questions only.
    const payloadAnswers = [];
    let missingRequired = 0;
    standards.forEach((std) => {
      visibleQuestionsFor(std).forEach((q) => {
        const ans = answers[q.uuid];
        const hasAnswer =
          q.answer_type === "single_choice"
            ? !!ans?.selected_option
            : !!ans?.text_answer;
        if (!hasAnswer) {
          if (q.is_required) missingRequired += 1;
          return;
        }
        payloadAnswers.push({
          ...(q.answer_type === "single_choice"
            ? { question: q.uuid, selected_option: ans.selected_option }
            : { question: q.uuid, text_answer: ans.text_answer }),
          ...(ans.comment ? { comment: ans.comment } : {}),
        });
      });
    });

    if (missingRequired > 0) {
      toast.error(t("observation.requiredUnanswered", { count: missingRequired }));
      return;
    }

    setIsSubmitting(true);
    try {
      // Conduct mode files against the BOOKED row, and that is the entire
      // point of the mode: creating a fresh draft here is what left every
      // booked visit behind as permanently overdue while its answers landed on
      // a different observation.
      // Otherwise, reuse a draft from a previous failed attempt; only create
      // when none exists yet, so a retry submits the same row instead of
      // spawning a new orphaned draft each time.
      let uuid = visitUuid || draftUuid;
      if (!uuid) {
        const draft = await createObservation({
          body: {
            observed_teacher: selectedTeacher.uuid,
            template_version: rubric.uuid,
            scheduled_date: selectedDate,
            lesson_topic: lessonTopic,
            group: selectedGroup || null,
            type: "official",
            // Explicit DRAFT: this is a live, in-progress form, not a visit
            // being booked ahead of time — without it the backend infers
            // SCHEDULED from scheduled_date and notifies the teacher of a
            // visit that (from their side) already happened.
            status: "draft",
          },
        });
        uuid = draft.uuid;
        setDraftUuid(uuid);
      }
      // The topic is typed at conduct time — every one of the 94 booked visits
      // in the database carries an empty one — and /submit/ accepts no header
      // fields, so it needs its own PATCH. So does a class picked for a visit
      // booked without one. Sent BEFORE the answers and only when something
      // actually changed: a refused header write must not leave behind a
      // scored observation whose topic or class quietly went missing.
      if (isConducting) {
        const header = {};
        if (lessonTopic !== (visit?.lesson_topic || "")) header.lesson_topic = lessonTopic;
        if (!classLocked && selectedGroup) header.group = selectedGroup;
        if (Object.keys(header).length > 0) {
          await updateObservation({ uuid, body: header });
        }
      }
      await submitObservation({
        uuid,
        body: {
          answers: payloadAnswers,
          // Local noon, so the picked calendar date is preserved when stored
          // as a UTC datetime regardless of the user's timezone.
          observed_at: new Date(`${selectedDate}T12:00:00`).toISOString(),
          // With the answers rather than in the header PATCH: it decides which
          // standards they are scored on, and a visit already in progress
          // takes no header write.
          ...(lessonSubject ? { domain_subject: lessonSubject } : {}),
          // One request, one transaction: a separate POST to /feedback/ could
          // fail after the observation is already submitted and scored.
          shared_note: sharedNote,
          private_note: privateNote,
        },
      });
      setDraftUuid(null);
      closeDraft();
      toast.success(t("observation.submitted"));
      // Land on the record that was just created, not on a dashboard that
      // does not mention it — otherwise submitting feels like the work went
      // nowhere.
      navigate("/my-observations");
    } catch (err) {
      // The server scores on the rubric's scope as it is NOW, and a standard
      // re-scoped since this form loaded changes what the lesson needs. The
      // rubric is reloaded so the form shows what the server scores.
      const refusal = SUBJECT_REFUSALS[apiErrorCode(err)];
      if (refusal) {
        refuseSubject(refusal);
        setRubricReload((n) => n + 1);
      } else {
        toast.error(formatApiError(err, t("observation.submitFailed")));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !rubric) {
    return (
      <div className="p-4 sm:p-6 bg-slate-50 min-h-full">
        <div className="max-w-4xl mx-auto space-y-4">
          <SkeletonCard className="h-40" lines={3} />
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} className="h-44" lines={4} />
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex-1 bg-slate-50 p-4 sm:p-6">
        <div className="max-w-6xl mx-auto py-24 text-center">
          <p className="text-sm text-rose-600 font-semibold">{loadError}</p>
          <div className="mt-3 flex justify-center">
            <InlineRetry onRetry={() => setRubricReload((n) => n + 1)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Work brought back from an earlier attempt that was never sent. Said
            out loud with its time, because answers appearing on a form the
            person thinks they are opening fresh would otherwise read as
            somebody else's. */}
        {restored && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
            <svg className="w-5 h-5 shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <p className="flex-1 min-w-0 text-sm font-semibold text-emerald-900">
              {t("observation.draftRestored", { time: savedAtLabel(restored.savedAt) })}
            </p>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-600 hover:bg-rose-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {t("observation.draftDiscard")}
            </button>
          </div>
        )}
        {confirmDiscard && (
          <ConfirmModal
            title={t("observation.draftDiscardTitle")}
            body={t("observation.draftDiscardBody")}
            confirmLabel={t("observation.draftDiscard")}
            danger
            onConfirm={discardDraft}
            onClose={() => setConfirmDiscard(false)}
          />
        )}

        {/* What is being conducted. */}
        {isConducting && visit && (
          <VisitBanner
            visit={visit}
            title={t("observation.conductingVisit")}
            subline={t("observation.conductingTeacher", {
              name: teacherName(visit.observed_teacher),
            })}
            t={t}
          >
            <VisitBrief visit={visit} rubric={rubric} subject={lessonSubject} t={t} />
          </VisitBanner>
        )}

        {/* A booking the caller already holds with this teacher (openVisits).
            "A different lesson" is a real answer, not a dismissal: an
            unplanned second visit is legitimate. */}
        {!isConducting && openVisit && (
          <VisitBanner
            visit={openVisit}
            title={t("observation.openVisitTitle")}
            subline={t("observation.openVisitBody")}
            t={t}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => setOpenVisits([])}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  {t("observation.openVisitDismiss")}
                </button>
                <button
                  type="button"
                  onClick={conductOpenVisit}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                    isConductDue(openVisit.scheduled_date) ? CONDUCT_BTN : CONDUCT_SOON_BTN
                  }`}
                >
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d={CONDUCT_ICON_PATH} />
                  </svg>
                  {t("upcoming.conduct")}
                </button>
              </>
            }
          />
        )}

        {/* Header. Pinned only from xl up, which is where the panel collapses
            into a single short row. Below that it stacks into a ~550px column,
            and a sticky block that tall covers most of a phone viewport — the
            questions then scroll underneath it instead of past it. */}
        <div className="xl:sticky xl:top-0 z-30 bg-white/95 backdrop-blur-sm p-4 sm:p-5 rounded-2xl shadow-md border border-slate-200 mb-6">
          <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 items-start xl:items-center">
            <div className="xl:max-w-[200px]">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">
                {t("observation.evaluationDashboard")}
              </p>
              <p className="text-sm font-bold text-slate-700 mt-1 leading-snug">
                {rubric?.template?.name || "—"}
              </p>
            </div>

            <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-6 w-full">
              {/* Guard moved out of the card: it used to wrap only the contents,
                  so with no teacher picked an empty bordered box still held open
                  a whole row of the panel. */}
              {selectedTeacher && (
                <div className="flex items-center gap-3 sm:gap-4 p-3 rounded-xl border border-slate-200 bg-slate-50 sm:min-w-[260px]">
                  <Avatar
                    user={selectedTeacher}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-indigo-600 text-white text-base sm:text-lg font-black shadow-sm shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 truncate">
                      {teacherName(selectedTeacher)}
                    </h3>
                    <p className="text-[10px] font-medium text-slate-500 truncate">
                      {selectedTeacher.organization?.name || selectedTeacher.email || ""}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3 sm:gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600 pl-1">
                    {t("observation.teacher")}
                  </label>
                  {isConducting ? (
                    <LockedField value={teacherName(visit?.observed_teacher) || "—"} />
                  ) : (
                    <>
                      <FieldSelect
                        value={selectedTeacherUuid}
                        onChange={pickTeacher}
                        options={teacherOptions}
                        searchable
                        onSearch={setTeacherQuery}
                        searchPlaceholder={t("common.search")}
                        ariaLabel={t("observation.teacher")}
                        placeholder={t("observation.selectTeacher")}
                        emptyText={
                          teachersError
                            ? t("observation.teachersLoadFailed")
                            : t("observation.noTeachers")
                        }
                        loading={teacherSearchPending}
                        loadingText={t("common.loading")}
                        selectedLabel={selectedTeacher ? teacherName(selectedTeacher) : ""}
                      />
                      {teachersError && (
                        <InlineRetry onRetry={() => setTeachersReload((n) => n + 1)} />
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600 pl-1">
                    {t("observation.class")}
                  </label>
                  {/* Read from the visit, not from `groups`: the class list is
                      still in flight when the banner is already on screen, and
                      a booked visit's class is not the observer's to change —
                      that is what Reschedule is for. A visit booked with no
                      class gets the picker instead (see classLocked). */}
                  {classLocked ? (
                    <LockedField value={visit?.group?.name || "—"} />
                  ) : (
                    <>
                      <FieldSelect
                        value={selectedGroup}
                        onChange={setSelectedGroup}
                        options={groupOptions}
                        ariaLabel={t("observation.class")}
                        placeholder={
                          classesError
                            ? t("observation.classesLoadFailed")
                            : selectedTeacher
                              ? t("observation.noClasses")
                              : t("observation.selectClassTeacherFirst")
                        }
                        disabled={groups.length === 0}
                      />
                      {classesError && (
                        <InlineRetry onRetry={() => setClassesReload((n) => n + 1)} />
                      )}
                    </>
                  )}
                </div>

                <div ref={subjectFieldRef} className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600 pl-1">
                    {t("observation.subject")}
                    {needsSubject && <RequiredMark />}
                  </label>
                  {subjectLocked ? (
                    <LockedField value={visit?.domain_subject?.name || "—"} />
                  ) : (
                    <>
                      <div className={`rounded-xl ${subjectInvalid ? "ring-2 ring-rose-500/30" : ""}`}>
                        <FieldSelect
                          value={lessonSubject}
                          onChange={setSelectedSubject}
                          options={subjectOptions}
                          searchable
                          searchPlaceholder={t("common.search")}
                          ariaLabel={t("observation.subject")}
                          placeholder={
                            subjectCatalog.failed
                              ? t("subjectScope.loadFailed")
                              : t("observation.selectSubject")
                          }
                          disabled={subjectCatalog.failed}
                        />
                      </div>
                      {subjectCatalog.failed && (
                        <InlineRetry onRetry={subjectCatalog.reload} />
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600 pl-1">
                    {t("observation.lessonTopic")}
                  </label>
                  {/* A locked topic gets the same padlock as the teacher and
                      class beside it. It used to be a readOnly input tinted one
                      shade greyer than the editable date next to it, explained
                      only by the banner sentence this change removed — which
                      left an in_progress visit (6 of them in the data) with a
                      field nothing could type into and nothing saying why. */}
                  {canEditVisitHeader ? (
                    <input
                      type="text"
                      placeholder={t("observation.lessonTopicPlaceholder")}
                      value={lessonTopic}
                      onChange={(e) => setLessonTopic(e.target.value)}
                      className="min-w-0 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 font-bold text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500 outline-none"
                    />
                  ) : (
                    <LockedField value={lessonTopic || "—"} />
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600 pl-1">
                    {t("observation.date")}
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="min-w-0 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 font-bold text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Suggested focus areas */}
        <div className="w-full bg-amber-50/40 border-2 border-amber-100 rounded-2xl shadow-md">
          <button
            type="button"
            onClick={() => setIsFocusExpanded((v) => !v)}
            className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 hover:bg-amber-100/30 transition-colors rounded-2xl"
          >
            <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
              <div className="w-9 h-9 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="text-left min-w-0">
                <h2 className="text-xs font-black text-amber-900 uppercase tracking-widest">
                  {t("teacher.suggestedFocusAreas")}
                </h2>
                <p className="text-[10px] font-bold text-amber-600/70">
                  {t("observation.focusSubtitle", {
                    name: selectedTeacher
                      ? teacherName(selectedTeacher)
                      : t("observation.thisTeacher"),
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0 w-full sm:w-auto justify-between sm:justify-end">
              {focusAreas.length > 0 && (
                <span className="text-[10px] font-black text-amber-600 bg-amber-100 px-2 py-1 rounded-full uppercase tracking-tighter">
                  {t("observation.priority", { count: focusAreas.length })}
                </span>
              )}
              <motion.div
                animate={{ rotate: isFocusExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-amber-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </motion.div>
            </div>
          </button>

          <AnimatePresence initial={false}>
            {isFocusExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  {focusAreas.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {focusAreas.map((area) => (
                        <div
                          key={area.key}
                          className="bg-white border border-amber-100 rounded-xl p-3 flex items-start gap-4 shadow-sm"
                        >
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            <span className="font-mono text-[10px] font-black bg-amber-50 text-amber-700 rounded px-2 py-0.5 border border-amber-100">
                              D{area.domainNumber}
                            </span>
                            <span className="text-[9px] text-amber-600 font-black bg-amber-100 px-1.5 rounded-full">
                              {area.count}x
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-tight mb-0.5 truncate">
                              {t("observation.domainTitle", { number: area.domainNumber, title: area.domainTitle })}
                            </p>
                            <p className="text-xs text-slate-800 font-bold leading-snug">
                              {area.fullText}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : focusError ? (
                    <div className="flex flex-col items-center gap-2 py-3">
                      <p className="text-xs font-black text-rose-500 uppercase tracking-widest text-center">
                        {t("observation.focusLoadFailed")}
                      </p>
                      <button
                        type="button"
                        onClick={() => setFocusReload((n) => n + 1)}
                        className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-800"
                      >
                        {t("common.retry")}
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest text-center py-3">
                      {focusResult?.insufficient_data
                        ? t("observation.focusEmptyInsufficient")
                        : t("observation.focusEmptyNone")}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Domain blocks */}
        {!selectedTeacher ? (
          <FormHint tone="indigo">{t("observation.pickTeacherFirst")}</FormHint>
        ) : subjectMissing ? (
          <FormHint tone="amber">{t("observation.pickSubjectHint")}</FormHint>
        ) : (
          noStandardApplies && (
            <FormHint tone="amber">{t("observation.noStandardForSubject")}</FormHint>
          )
        )}
        <div
          className={`space-y-4 transition-opacity ${canAnswer ? "" : "opacity-50"}`}
          inert={!canAnswer}
        >
          {standards.map((std, stdIdx) => {
            const visibleQuestions = visibleQuestionsFor(std);
            // The strip below renders one segment per visible question (N/O
            // included), but the score must mirror the backend: "Not observed"
            // answers are excluded from the denominator, never counted as a No.
            const segmentCount = visibleQuestions.length;
            const scorableOpts = visibleQuestions
              .map((q) =>
                optionsFor(q).find(
                  (o) => o.uuid === answers[q.uuid]?.selected_option,
                ),
              )
              .filter((opt) => !opt?.is_not_observed);
            const total = scorableOpts.length;
            const points = scorableOpts.filter((opt) => opt?.is_positive).length;
            const ratio = total > 0 ? points / total : 0;
            const scoreColor =
              ratio >= 0.8
                ? "text-emerald-600"
                : ratio >= 0.6
                ? "text-lime-600"
                : ratio >= 0.4
                ? "text-amber-600"
                : ratio >= 0.2
                ? "text-orange-600"
                : "text-rose-600";

            return (
              <div
                key={std.uuid}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/30">
                  <div className="flex justify-between items-center">
                    <h2 className="text-sm font-bold text-slate-800 flex flex-wrap items-center gap-2">
                      {t("observation.domainTitle", { number: std.number || stdIdx + 1, title: std.title })}
                      {focusStandardUuids.has(std.uuid) && (
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className="w-2 h-2 bg-amber-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.6)] animate-pulse" />
                          <span className="text-[9px] font-black text-amber-600 uppercase tracking-tighter">
                            {t("observation.focusAreasLabel")}
                          </span>
                        </span>
                      )}
                    </h2>
                  </div>

                  <div className="mt-2 flex items-center gap-4">
                    <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden flex">
                      {visibleQuestions.map((q) => {
                        const ans = answers[q.uuid];
                        const opt = optionsFor(q).find(
                          (o) => o.uuid === ans?.selected_option,
                        );
                        return (
                          <div
                            key={q.uuid}
                            className="h-full border-r border-white/20 last:border-none transition-all duration-300"
                            style={{
                              width: `${100 / Math.max(segmentCount, 1)}%`,
                              backgroundColor: opt?.color || "transparent",
                            }}
                          />
                        );
                      })}
                    </div>
                    <span
                      className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap shrink-0 ${scoreColor}`}
                    >
                      {t("observation.points", { points, total })}
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  <AnimatePresence initial={false}>
                    {visibleQuestions.map((q, qIdx) => {
                      const ans = answers[q.uuid];
                      const options = optionsFor(q);
                      const isLowAnswer =
                        String(
                          options.find((o) => o.uuid === ans?.selected_option)?.value ?? "",
                        ).toLowerCase() === "no";
                      // Opens by itself once a question is answered "no" — that
                      // is where "what exactly, and why" is worth typing — and
                      // stays wherever the observer puts it after that.
                      const isCommentOpen =
                        openComments[q.uuid] ?? (Boolean(ans?.comment) || isLowAnswer);
                      const isDependent =
                        (q.visibility_conditions || []).length > 0;
                      const isFocus = focusQuestionUuids.has(q.uuid);
                      const hiddenFollowUps = (dependentsByUuid[q.uuid] || []).filter(
                        (u) => !visibleUuids.has(u),
                      ).length;
                      return (
                        <motion.div
                          key={q.uuid}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div
                            className={`px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 transition-colors ${
                              isDependent ? "pl-6 md:pl-10 border-l-[3px] border-indigo-200" : ""
                            } ${
                              isFocus
                                ? "ring-1 ring-inset ring-amber-300 bg-amber-50/40"
                                : isDependent
                                ? "bg-indigo-50/30"
                                : ""
                            }`}
                          >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            {isDependent && (
                              <svg
                                className="w-3 h-3 text-indigo-400 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M9 5l-5 5m0 0l5 5m-5-5h11a4 4 0 014 4v2"
                                />
                              </svg>
                            )}
                            <span className="text-[9px] font-bold text-slate-500 uppercase">
                              {t("observation.questionNum", { std: std.number || stdIdx + 1, q: qIdx + 1 })}
                            </span>
                            {q.is_required && (
                              <span className="text-[8px] font-black bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                {t("observation.required")}
                              </span>
                            )}
                            {isFocus && (
                              <span className="inline-flex items-center gap-1 text-[8px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.77l-5.2 2.73.99-5.79-4.21-4.1 5.82-.85L10 1.5z" />
                                </svg>
                                {t("observation.focusArea")}
                              </span>
                            )}
                            <HiddenFollowUpsBadge
                              count={hiddenFollowUps}
                              labelKey="observation.hiddenFollowUps"
                              hintKey="observation.hiddenFollowUpsHint"
                            />
                          </div>
                          <p className={`text-sm font-medium leading-tight ${isFocus ? "text-amber-900" : "text-slate-700"}`}>
                            {q.text}
                          </p>
                          {q.help_text && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {q.help_text}
                            </p>
                          )}
                        </div>

                        {q.answer_type === "single_choice" ? (
                          <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                            {options.map((opt) => {
                              const isActive = ans?.selected_option === opt.uuid;
                              return (
                                <button
                                  key={opt.uuid}
                                  type="button"
                                  onClick={() => setAnswer(q, opt.uuid)}
                                  style={
                                    isActive
                                      ? {
                                          backgroundColor: opt.color,
                                          borderColor: opt.color,
                                        }
                                      : undefined
                                  }
                                  className={`px-3 py-2 md:py-1.5 rounded-lg border text-[11px] font-bold transition-all ${
                                    isActive
                                      ? "text-white shadow-sm"
                                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                                  }`}
                                >
                                  {opt.short_label || opt.label}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={ans?.text_answer || ""}
                            onChange={(e) => setAnswer(q, e.target.value)}
                            placeholder={t("observation.answerPlaceholder")}
                            className="w-full md:w-64 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 md:py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500 outline-none"
                          />
                        )}

                        {/* Icon, not a labelled row: this repeats under every
                            question, and seven copies of the same sentence is
                            the loudest thing on a form whose subject is the
                            lesson. Filled once a note exists, so the questions
                            carrying one are scannable down the page. */}
                        <button
                          type="button"
                          onClick={() =>
                            setOpenComments((prev) => ({ ...prev, [q.uuid]: !isCommentOpen }))
                          }
                          aria-expanded={isCommentOpen}
                          title={t("observation.addComment")}
                          aria-label={
                            ans?.comment
                              ? t("observation.commentLabel")
                              : t("observation.addComment")
                          }
                          className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                            ans?.comment || isCommentOpen
                              ? "bg-indigo-50 text-indigo-600"
                              : "text-slate-300 hover:bg-slate-100 hover:text-indigo-600"
                          }`}
                        >
                          <svg
                            aria-hidden="true"
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 10h8M8 14h5m-9 7l3.5-3.5H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v15z"
                            />
                          </svg>
                        </button>
                          </div>
                          {isCommentOpen && (
                            <textarea
                              rows={2}
                              value={ans?.comment || ""}
                              onChange={(e) => setAnswerComment(q, e.target.value)}
                              placeholder={t("observation.commentPlaceholder")}
                              aria-label={t("observation.commentLabel")}
                              className="mt-2 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-700 focus:ring-2 focus:ring-indigo-400 outline-none resize-y"
                            />
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>

        {/* Final heatmap summary */}
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-slate-50 text-slate-500 rounded-xl flex items-center justify-center shrink-0 shadow-sm border border-slate-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                {t("observation.finalHeatmap")}
              </h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                {t("observation.finalHeatmapSub")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {standards.map((std, idx) => {
              const visibleQuestions = visibleQuestionsFor(std);
              return (
                <div
                  key={std.uuid}
                  className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                      {t("observation.domain", { number: std.number || idx + 1 })}
                    </span>
                    <p
                      className="text-[10px] font-bold text-slate-700 line-clamp-2 pr-2 leading-tight"
                      title={std.title}
                    >
                      {std.title}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {visibleQuestions.map((q) => {
                      const ans = answers[q.uuid];
                      const opt = optionsFor(q).find(
                        (o) => o.uuid === ans?.selected_option,
                      );
                      return (
                        <div
                          key={q.uuid}
                          className="w-3 h-3 rounded-[2px] shadow-sm"
                          style={{ backgroundColor: opt?.color || "#e2e8f0" }}
                          title={q.text}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Write-up: two audiences, said out loud on each field. */}
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-200 space-y-5">
          <div>
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
              {t("observation.notesHeading")}
            </h2>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              {t("observation.notesSub")}
            </p>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <label
              htmlFor="observation-shared-note"
              className="text-xs font-black text-emerald-800 uppercase tracking-wider"
            >
              {t("observation.sharedNoteTitle")}
            </label>
            <p className="text-[11px] text-emerald-700/80 font-medium mt-0.5 mb-2">
              {t("observation.sharedNoteHint")}
            </p>
            <textarea
              id="observation-shared-note"
              rows={4}
              value={sharedNote}
              onChange={(e) => setSharedNote(e.target.value)}
              placeholder={t("observation.sharedNotePlaceholder")}
              className="w-full bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500 outline-none resize-y"
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <label
              htmlFor="observation-private-note"
              className="text-xs font-black text-slate-700 uppercase tracking-wider"
            >
              {t("observation.privateNoteTitle")}
            </label>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5 mb-2">
              {t("observation.privateNoteHint")}
            </p>
            <textarea
              id="observation-private-note"
              rows={4}
              value={privateNote}
              onChange={(e) => setPrivateNote(e.target.value)}
              placeholder={t("observation.privateNotePlaceholder")}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-slate-400 outline-none resize-y"
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 pt-6 pb-12">
          {rubricFailed && (
            <div className="flex items-center gap-3 text-sm text-rose-600 font-semibold">
              <span>{t("observation.loadFailed")}</span>
              <InlineRetry onRetry={() => setRubricReload((n) => n + 1)} />
            </div>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || rubricStale}
            className="w-full sm:w-auto px-8 sm:px-12 py-4 bg-emerald-600 text-white rounded-2xl font-bold text-base sm:text-lg shadow-xl hover:bg-emerald-700 hover:shadow-2xl transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {isSubmitting ? t("observation.submitting") : t("observation.submitFinal")}
          </button>
        </div>
      </div>
    </div>
  );
}
